import { ascend, descend, sortWith } from 'ramda';

import { SortOrder, SortOrderType } from '../types';

/**
 * see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
 * example - order of characters using following kinds of normalization:
 *            NFD:  a b c č s š z
 *  NFC (default):  a b c s z č š
 */
const stringNormalizationForm = 'NFD';

export const prepareValue = <T, K extends keyof T>(item: T, property: K) =>
  String(item[property]).toLowerCase().normalize(stringNormalizationForm);

interface CustomSort {
  // Array of objects
  <T, K extends keyof T>(data: T[], properties: K[], order: SortOrderType): T[];

  // Record object & array of objects' ids
  <T, K extends keyof T, V extends string>(
    data: Record<V, T>,
    properties: K[],
    order: SortOrderType,
    ids: string[]
  ): V[];
}

export const customSort: CustomSort = <T, K extends keyof T, V extends string>(
  data: T[] | Record<V, T>,
  properties: K[],
  order: SortOrderType,
  ids?: V[]
) => {
  const sorterFn = order === SortOrder.ASCENDING ? ascend : descend;

  if (Array.isArray(data)) {
    return sortWith(
      properties.map((property) => sorterFn<T>((item) => prepareValue(item, property))),
      data
    );
  }

  if (ids) {
    const { verifiedIds, notFoundIds } = ids.reduce(
      (result, id) => {
        if (data[id]) {
          result.verifiedIds.push(id);
        } else {
          result.notFoundIds.push(id);
        }
        return result;
      },
      { verifiedIds: [] as V[], notFoundIds: [] as V[] }
    );

    if (notFoundIds.length) {
      console.warn(
        `customSort: provided ids [${notFoundIds.map((id) => `'${id}'`).join(', ')}] not found in record object`
      );
    }

    return sortWith(
      properties.map((prop) => sorterFn<V>((itemId) => prepareValue(data[itemId], prop))),
      verifiedIds
    );
  }

  return [];
};
